import { saveAttachmentAsOnSharepoint, SaveAttachmentAsOnSharePointModel } from "client/Api/Email/sharePointAttachments";
import { saveAttachmentAsOnWebDav, SaveAttachmentAsOnWebDavModel } from "client/Api/Email/webDavAttachments";
import {
	SaveAsAttachmentResponseModel,
	SaveAttachmentData,
	useSaveAttachmentDialog
 } from "client/Features/Documents/Dialogs/SaveAttachmentDialog";
import { AttachmentModel } from "client/Api/Email/email";
import { MailMessageLocationModel } from "client/Api/Email/common";
import { DocumentStoreType } from "client/Api/Documents/common";
import { ReactElement, useCallback } from "react";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";

export type SaveAttachmentAsHandlerOptions = {
	attachment: AttachmentModel;
	messageLocation: MailMessageLocationModel;
};

export function useSaveAttachmentAsHandler(options: SaveAttachmentAsHandlerOptions): {
	saveAttachmentAsHandler: () => void;
	saveAttachmentDialog: ReactElement | null;
} {
	const {
		attachment: { exchangeAttachmentId, name },
		messageLocation
	} = options;

	const [saveAttachmentDialog, openSaveAttachmentDialog] = useSaveAttachmentDialog();

	const saveAttachmentAsHandler = useCallback(
		(): void => {
			openSaveAttachmentDialog({
				name,
				onSave: (saveData) => saveAttachmentAs(
					messageLocation,
					exchangeAttachmentId,
					saveData
				)
			});
		},
		[
			exchangeAttachmentId,
			messageLocation,
			name,
			openSaveAttachmentDialog
		]
	);

	return {
		saveAttachmentAsHandler,
		saveAttachmentDialog
	};
}

function saveAttachmentAs(
	messageLocation: MailMessageLocationModel,
	exchangeAttachmentId: string,
	saveData: SaveAttachmentData
): Promise<SaveAsAttachmentResponseModel> {
	switch (saveData.location?.kind) {
		case DocumentStoreType.SharePoint:
			const sharePointModel: SaveAttachmentAsOnSharePointModel = {
				name: saveData.name,
				folderId: saveData.location.folder.id,
				location: convertToLocationModel(
					saveData.location
						.folder
						.location
				),
				message: messageLocation,
				exchangeAttachmentId: exchangeAttachmentId
			};
			return saveAttachmentAsOnSharepoint(sharePointModel);
		case DocumentStoreType.WebDav:
			const webDavModel: SaveAttachmentAsOnWebDavModel = {
				name: saveData.name,
				folderId: saveData.location.folder.id,
				serverId: saveData.location.folder.server.id,
				message: messageLocation,
				exchangeAttachmentId: exchangeAttachmentId
			};
			return saveAttachmentAsOnWebDav(webDavModel);
		default:
			throw new Error("Unknown location kind");
	}
}